/**
 * MarkTaughtButton — Phase 108.C "Mark as taught" control. Used on the
 * Lesson Workspace header, every library row, and the Planner.
 *
 * Behaviour:
 *   - Click while currently `taught=false`:
 *       - For a Math Engine unit-anchor activity (math_unit_summary /
 *         math_end_unit_test etc. whose `cascade-preview` reports
 *         siblings) we open a confirmation modal showing how many
 *         lessons will be flipped — per the spec, cascade is a
 *         convenience default, not silent magic.
 *       - For everything else we flip immediately.
 *   - Click while currently `taught=true`: silently unmark (toast only).
 *
 * The button is *self-fetching* for the cascade preview so the parent
 * doesn't have to plumb anything beyond the activity object and an
 * `onChange` callback to refresh whatever surface owns the data.
 */
import { useState } from "react";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import {
  markActivityTaught,
  previewTaughtCascade,
  unmarkActivityTaught,
} from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";

const SIZE_CLASSES = {
  sm: "h-7 px-2 text-[10px] gap-1",
  md: "h-9 px-3 text-xs gap-1.5",
};

export const MarkTaughtButton = ({
  activity,
  onChange,
  size = "md",
  className = "",
}) => {
  const [busy, setBusy] = useState(false);
  const [cascadeModal, setCascadeModal] = useState(null); // null | preview obj
  const isTaught = !!activity?.taught;
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  // A culminating task is assessed, not taught — relabel the control on a
  // summative_task. Behaviour is IDENTICAL (same endpoint, same `taught`
  // flag): this feeds Live Reports / recently-taught / summative-suggestions,
  // NOT the Coverage Tracker counter (which counts every term activity by
  // created_at regardless of this flag). Rename is cosmetic only.
  const isSummative = activity?.output_type === "summative_task";
  const verbUnmarked = isSummative ? "Mark Assessed" : "Mark Taught";
  const verbMarked = isSummative ? "Assessed" : "Taught";

  if (!activity?.id) return null;

  const finalize = (newTaught, affected = 1) => {
    const verb = isSummative ? "assessed" : "taught";
    if (newTaught) {
      toast.success(
        affected > 1
          ? `Marked ${affected} activities as ${verb}`
          : `Marked as ${verb}`,
      );
    } else {
      toast.success(`Marked as not ${verb}`);
    }
    if (onChange) onChange();
  };

  const doMark = async (cascade) => {
    setBusy(true);
    try {
      const res = await markActivityTaught(activity.id, { cascade });
      finalize(true, (res?.affected_ids || []).length || 1);
    } catch (err) {
      toast.error(
        "Couldn't mark taught",
        { description: String(err?.response?.data?.detail || err?.message || "") },
      );
    } finally {
      setBusy(false);
      setCascadeModal(null);
    }
  };

  const doUnmark = async () => {
    setBusy(true);
    try {
      await unmarkActivityTaught(activity.id);
      finalize(false);
    } catch (err) {
      toast.error(
        "Couldn't update",
        { description: String(err?.response?.data?.detail || err?.message || "") },
      );
    } finally {
      setBusy(false);
    }
  };

  const handleClick = async () => {
    if (busy) return;
    if (isTaught) {
      await doUnmark();
      return;
    }
    // Look up cascade preview — if this is a Math Engine unit with
    // siblings, prompt for confirmation before sweeping them.
    setBusy(true);
    try {
      const preview = await previewTaughtCascade(activity.id);
      if (preview?.is_math_unit && preview.sibling_count > 0) {
        setCascadeModal(preview);
        setBusy(false);
        return;
      }
    } catch {
      // Fall back to simple flip if the preview call fails.
    }
    setBusy(false);
    await doMark(false);
  };

  const baseClass = `inline-flex items-center rounded-full font-bold uppercase tracking-[0.12em] transition border ${sizeCls} ${
    isTaught
      ? "bg-[#1c9e7e] text-white border-[#1c9e7e] hover:bg-[#178469]"
      : "bg-white text-[#4a6984] border-[#4a6984]/25 hover:bg-[#96ded1]/15 hover:border-[#96ded1]"
  } ${className}`;

  return (
    <>
      <button
        type="button"
        data-testid={`mark-taught-${activity.id}`}
        aria-pressed={isTaught}
        onClick={handleClick}
        disabled={busy}
        title={
          isTaught
            ? `Confirmed ${isSummative ? "assessed" : "taught"} — click to undo. Eligible for Live Reports.`
            : `Mark this ${isSummative ? "task assessed" : "lesson taught"} so its expectations feed Live Reports.`
        }
        className={baseClass}
      >
        {busy ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : isTaught ? (
          <Sparkles className="h-3 w-3" />
        ) : (
          <CheckCircle2 className="h-3 w-3" />
        )}
        <span>{isTaught ? verbMarked : verbUnmarked}</span>
      </button>

      <Dialog
        open={!!cascadeModal}
        onOpenChange={(o) => {
          if (!o) setCascadeModal(null);
        }}
      >
        <DialogContent data-testid="mark-taught-cascade-modal">
          <DialogHeader>
            <DialogTitle>Mark this unit taught?</DialogTitle>
            <DialogDescription className="pt-1">
              {cascadeModal?.sibling_count}{" "}
              {cascadeModal?.sibling_count === 1 ? "lesson" : "lessons"} in this
              unit will also be marked as taught. Any lessons you&apos;d like to
              skip can be individually un-marked afterwards — the cascade is a
              convenience, not a lock.
            </DialogDescription>
          </DialogHeader>
          {cascadeModal?.sibling_preview?.length ? (
            <ul
              data-testid="mark-taught-cascade-list"
              className="my-3 max-h-48 overflow-y-auto space-y-1.5 rounded-md border border-[#4a6984]/15 bg-[#f4f5f7]/40 p-3"
            >
              {cascadeModal.sibling_preview.map((s) => (
                <li
                  key={s.id}
                  className="text-xs text-[#1c2d3d] flex items-center justify-between gap-2"
                >
                  <span className="truncate">{s.activity_name}</span>
                  {s.taught ? (
                    <span className="text-[10px] text-[#4a6984]/65 font-bold uppercase tracking-[0.1em]">
                      already taught
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {cascadeModal?.already_taught_count > 0 ? (
            <p className="text-[11px] text-[#4a6984]/75 -mt-2">
              {cascadeModal.already_taught_count}{" "}
              {cascadeModal.already_taught_count === 1 ? "lesson is" : "lessons are"}
              {" "}
              already marked — their taught date will not be overwritten.
            </p>
          ) : null}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCascadeModal(null)}
              data-testid="mark-taught-cascade-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => doMark(true)}
              data-testid="mark-taught-cascade-confirm"
              className="bg-[#1c9e7e] hover:bg-[#178469] text-white"
            >
              Mark unit taught
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MarkTaughtButton;
